import { useEffect, useMemo, useState } from "react";
import "./roster.css";
import { OfferJob } from "./OfferJob";
import { BidsPanel } from "./BidsPanel";
import { LienWaiversPanel } from "./LienWaiversPanel";
import { JobControlPanel } from "./JobControlPanel";
import { ExecApproval } from "./ExecApproval";
import { NetworkLaunchCard } from "./NetworkLaunchCard";

const API = process.env.REACT_APP_BACKEND_URL;
const STAGE = { active: "Active", payout_pending: "Payout setup", intake: "Intake received", invited: "Invited", expired: "Invite expired" };
const INS = { valid: "Insured", expiring: "Expiring", expired: "Expired", missing: "No COI" };

const Stat = ({ label, value, tone, tid }) => (
  <div className={`rr-stat rr-stat--${tone || "base"}`} data-testid={tid}><b>{value}</b><span>{label}</span></div>
);

export default function ReadyRosterTab({ setTab }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [trade, setTrade] = useState("all");
  const [onlyReady, setOnlyReady] = useState(false);
  const [orders, setOrders] = useState([]);

  const load = () => fetch(`${API}/api/admin/trade-network/roster`, { credentials: "include" })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Roster unavailable (${r.status})`))))
    .then(setD).catch((e) => setErr(e.message));

  useEffect(() => {
    load();
    fetch(`${API}/api/vendor-dispatch/orders`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : {})).then((j) => setOrders((j.orders || j.work_orders || []).filter((o) => ["dispatched", "offered"].includes(o.status) && !/^TEST/i.test(o.property_address || "") && !/^TEST/i.test(o.work_order_id || ""))))
      .catch(() => setOrders([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => (d?.partners || []).filter((r) => (trade === "all" || r.trade === trade) && (!onlyReady || r.work_ready)), [d, trade, onlyReady]);

  if (err) return <div className="rr-wrap rr-err" data-testid="roster-error">{err}</div>;
  if (!d) return <div className="rr-wrap rr-muted" data-testid="roster-loading">Loading roster…</div>;
  const t = d.totals;

  return (
    <div className="rr-wrap" data-testid="ready-roster-tab">
      <div className="rr-eyebrow">Ready Roster</div>
      <h2 className="rr-h">Who can mobilize the day a contract lands</h2>
      <NetworkLaunchCard />
      <BidsPanel />
      <LienWaiversPanel />
      <JobControlPanel />
      <div className="rr-stats">
        <Stat label="Work-ready" value={t.work_ready} tone="ok" tid="roster-work-ready" />
        <Stat label="Active partners" value={t.active} tid="roster-active" />
        <Stat label="In onboarding" value={t.partners - t.active} tid="roster-onboarding" />
        <Stat label="Insurance attention" value={t.insurance_attention} tone={t.insurance_attention ? "warn" : "base"} tid="roster-insurance-attention" />
        <button type="button" className="rr-stat rr-stat--link" onClick={() => setTab && setTab("vendor-network")} data-testid="roster-pending-referrals">
          <b>{t.pending_referrals}</b><span>Partner referrals to review →</span>
        </button>
      </div>
      {d.trades.length > 0 && (
        <div className="rr-trades" data-testid="roster-trade-coverage">
          <button type="button" className={`rr-chip ${trade === "all" ? "on" : ""}`} onClick={() => setTrade("all")} data-testid="roster-trade-all">All trades</button>
          {d.trades.map((x) => (
            <button key={x.trade} type="button" className={`rr-chip ${trade === x.trade ? "on" : ""} ${x.work_ready ? "" : "gap"}`} onClick={() => setTrade(x.trade)} data-testid={`roster-trade-${x.trade.replace(/\s+/g, "-")}`}>
              {x.trade} · {x.work_ready} ready{x.pending ? ` · ${x.pending} pending` : ""}
            </button>
          ))}
        </div>
      )}
      <label className="rr-toggle"><input type="checkbox" checked={onlyReady} onChange={(e) => setOnlyReady(e.target.checked)} data-testid="roster-only-ready" /> Show work-ready only</label>
      {rows.length === 0 ? (
        <div className="rr-empty" data-testid="roster-empty">No partners yet. Invites sent from Vendor Network appear here as each trade moves through onboarding.</div>
      ) : (
        <div className="rr-table" data-testid="roster-table">
          {rows.map((r) => (
            <div key={r.partner_id} className="rr-row" data-testid={`roster-row-${r.partner_id}`}>
              <div className="rr-name"><b>{r.name || "—"}</b><span>{r.business || r.entity_type?.replace(/_/g, " ") || ""}</span></div>
              <div className="rr-cell">{r.trade}{r.day_rate_usd ? <span className="rr-muted"> · ${r.day_rate_usd}/day</span> : null}</div>
              <div className="rr-cell rr-muted">{r.service_area || "—"}</div>
              <div className={`rr-tag rr-tag--${r.stage}`} data-testid={`roster-stage-${r.partner_id}`}>{STAGE[r.stage]}</div>
              {r.courier_eligible && <div className="rr-tag rr-tag--active" data-testid={`roster-courier-${r.partner_id}`}>Courier-capable</div>}
              <div className={`rr-tag ${r.agreement_signed ? "rr-tag--active" : "rr-tag--invited"}`} data-testid={`roster-agreement-${r.partner_id}`}>{r.agreement_signed ? "Agreement signed" : "Agreement unsigned"}</div>
              <ExecApproval r={r} onDone={load} />
              <div className={`rr-tag rr-tag--ins-${r.insurance}`} title={r.insurance_expires || ""}>{INS[r.insurance]}</div>
              <div className="rr-cell rr-contact">{r.phone ? <a href={`tel:${r.phone}`}>{r.phone}</a> : null}{r.email ? <a href={`mailto:${r.email}`}>{r.email}</a> : null}</div>
              {r.work_ready && <OfferJob partnerId={r.partner_id} orders={orders.filter((o) => o.trade_lane === r.trade_lane)} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
